import React, { useState } from "react";
import Moment from "react-moment";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import Amount from "../../../ui-components/molecules/amount/amount";
import Tag from "../../../ui-components/molecules/tag/tag";
import { PaymentDetail, PaymentEventItem } from "../../../app/data/payment/PaymentSession";
import { statusColor } from "./paymentAdminUtils";

export interface PaymentDetailSidebarProps {
  show: boolean;
  isLoading: boolean;
  detail: PaymentDetail | null;
  detailError: string | null;
  onClose: () => void;
}

const PaymentDetailSidebar: React.FC<PaymentDetailSidebarProps> = ({
  show,
  isLoading,
  detail,
  detailError,
  onClose,
}) => {
  const [expandedPayloads, setExpandedPayloads] = useState<Record<string, boolean>>({});

  const togglePayload = (eventId: string) => {
    setExpandedPayloads((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const payment = detail?.payment;
  const headerLabel = payment
    ? `Payment detail · ${payment.status}`
    : "Payment detail";

  const totalChargedCents =
    payment?.amountCents != null
      ? (payment.amountCents || 0) + (payment.surchargeAmountCents || 0)
      : null;

  return (
    <SlideOutSidebar
      show={show}
      onClose={onClose}
      header={headerLabel}
      size="medium-860"
      spinner={isLoading}
    >
      {detailError && <XGSErrorMessage>{detailError}</XGSErrorMessage>}
      {payment && (
        <>
          <div className="xgs-sidebar__details__rows">
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Payment ID</div>
              <div className="xgs-sidebar__details__value xgs-payment-admin__id">
                {payment.paymentId}
              </div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Status</div>
              <div className="xgs-sidebar__details__value">
                <Tag mods={{ color: statusColor(payment.status) }}>{payment.status}</Tag>
              </div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Provider</div>
              <div className="xgs-sidebar__details__value">{payment.provider || "—"}</div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Account</div>
              <div className="xgs-sidebar__details__value">{payment.accountNumber ?? "—"}</div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Invoices</div>
              <div className="xgs-sidebar__details__value">
                {(payment.invoiceNumbers || []).join(", ") || "—"}
              </div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Amount</div>
              <div className="xgs-sidebar__details__value">
                {payment.amountCents != null ? (
                  <Amount amount={payment.amountCents / 100} />
                ) : (
                  "—"
                )}
              </div>
            </div>
            {payment.surchargeAmountCents != null && (
              <div className="xgs-sidebar__details__row">
                <div className="xgs-sidebar__details__label">Surcharge</div>
                <div className="xgs-sidebar__details__value">
                  <Amount amount={payment.surchargeAmountCents / 100} />
                </div>
              </div>
            )}
            {totalChargedCents != null && (
              <div className="xgs-sidebar__details__row">
                <div className="xgs-sidebar__details__label">Total charged</div>
                <div className="xgs-sidebar__details__value xgs-payment-admin__highlight-value">
                  <Amount amount={totalChargedCents / 100} />
                </div>
              </div>
            )}
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">External payment</div>
              <div className="xgs-sidebar__details__value">
                {payment.externalPaymentId || "—"}
              </div>
            </div>
            <div className="xgs-sidebar__details__row">
              <div className="xgs-sidebar__details__label">Link ID</div>
              <div className="xgs-sidebar__details__value">
                {payment.externalLinkId || "—"}
              </div>
            </div>
            {payment.dateCreated && (
              <div className="xgs-sidebar__details__row">
                <div className="xgs-sidebar__details__label">Created</div>
                <div className="xgs-sidebar__details__value">
                  <Moment format="MM/DD/YYYY h:mm:ss A">{payment.dateCreated}</Moment>
                </div>
              </div>
            )}
          </div>

          <h4 className="xgs-payment-admin__timeline-heading">Event timeline</h4>
          <ul className="xgs-payment-admin__timeline">
            {(detail?.events || []).map((ev: PaymentEventItem) => (
              <li key={ev.id} className="xgs-payment-admin__timeline__item">
                <div className="xgs-payment-admin__timeline__timestamp">
                  {ev.dateCreated ? (
                    <Moment format="MM/DD/YYYY h:mm:ss A">{ev.dateCreated}</Moment>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="xgs-payment-admin__timeline__body">
                  <div className="xgs-payment-admin__timeline__summary">
                    {ev.eventType} ({ev.fromStatus || "—"} → {ev.toStatus || "—"}) by {ev.actor}
                    {ev.gatewayPayloadRef ? ` [${ev.gatewayPayloadRef}]` : ""}
                  </div>
                  {ev.gatewayPayload && (
                    <>
                      <button
                        type="button"
                        className="xgs-payment-admin__payload-toggle"
                        onClick={() => togglePayload(ev.id)}
                      >
                        {expandedPayloads[ev.id] ? "Hide gateway payload" : "Show gateway payload"}
                      </button>
                      {expandedPayloads[ev.id] && (
                        <pre className="xgs-payment-admin__payload">{ev.gatewayPayload}</pre>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
            {!detail?.events?.length && (
              <li className="xgs-payment-admin__timeline__item">No events recorded.</li>
            )}
          </ul>
        </>
      )}
    </SlideOutSidebar>
  );
};

export default PaymentDetailSidebar;
